import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import StripeUpdateCard from './StripeUpdateCard';
import CardFields from './CardFields';
import ModalWrapper from '../shared/ModalWrapper';
import UpdateCardLoadingState from './UpdateCardLoadingState';
import UpdateCardErrorState from './UpdateCardErrorState';
import useStripeLoader, { clearStripeCache } from '../../hooks/useStripeLoader';
import { WEBHOOK_PROCESSING_DELAY_MS } from '../../utils/constants';

/**
 * Modal component for updating credit card information via Stripe, used where
 * navigating away would lose in-progress state (e.g. mid-checkout). Everywhere
 * else card updates go through the standalone /update-card page.
 * Handles Stripe loading states, errors, and successful card updates
 * @param {object} props - Component props
 * @param {object} props.user - User object containing card information
 * @param {string} props.user.id - User ID
 * @param {string} props.user.cardType - Card type (e.g., 'Visa', 'MasterCard')
 * @param {string} props.user.cardExpiry - Card expiry date
 * @param {string} props.user.cardLast4 - Last 4 digits of card
 * @param {string} [props.customerId] - Stripe customer ID
 * @param {boolean} [props.isGrace] - Whether user is in grace period (affects button text)
 * @param {Function} props.handleCloseModal - Callback when modal closes
 * @param {Function} props.handleNestedLoadingState - Callback for nested loading state changes
 * @param {Function} props.setModalIsOpen - Function to control modal open state
 * @param {boolean} props.modalIsOpen - Whether modal is currently open
 * @returns {JSX.Element} React component
 */
export default function UpdateCardModal({
    user,
    customerId,
    isGrace,
    handleCloseModal,
    handleNestedLoadingState,
    setModalIsOpen,
    modalIsOpen,
}) {
    const [modalLoadingState, setModalLoadingState] = useState(false);
    const buttonText = isGrace ? 'Update' : 'Edit';
    const openModalTrigger = (
        <button className="staticfield-link" data-test-btn="open-card">
            {buttonText}
        </button>
    );

    // Load Stripe only when modal opens
    const {
        stripe,
        loading: stripeLoading,
        error: stripeError,
        loadStripe,
    } = useStripeLoader({
        loadOnMount: false,
    });

    // Trigger Stripe loading when modal is about to open or is open
    useEffect(() => {
        const stripeNotYetLoaded = !stripe && !stripeLoading && !stripeError;

        if (modalIsOpen && stripeNotYetLoaded) {
            loadStripe();
        }
    }, [modalIsOpen, stripe, stripeLoading, stripeError, loadStripe]);

    /**
     * Handle retry when Stripe loading fails
     * Clears Stripe cache and attempts to reload
     */
    const handleRetry = () => {
        clearStripeCache();
        loadStripe();
    };

    /**
     * Handle successful stripe card update
     * Closes the modal with a reload delay for webhook processing
     */
    const handleStripeUpdateSuccess = () => {
        handleCloseModal({ reload: true, delay: WEBHOOK_PROCESSING_DELAY_MS });
    };

    /**
     * Render the appropriate modal content based on Stripe loading state
     * @returns {JSX.Element} Modal content component
     */
    const loadingTitle = isGrace ? 'Complete your payment' : undefined;

    const renderModalContent = () => {
        if (stripeLoading) {
            return (
                <UpdateCardLoadingState
                    message="Loading payment processor..."
                    title={loadingTitle}
                />
            );
        }

        if (stripeError) {
            return <UpdateCardErrorState stripeError={stripeError} onRetry={handleRetry} />;
        }

        if (stripe) {
            return (
                <StripeUpdateCard
                    setLoadingState={handleNestedLoadingState}
                    onCloseModal={handleStripeUpdateSuccess}
                    userId={user?.id}
                    customerId={customerId}
                    setModalLoadingState={setModalLoadingState}
                    isGrace={isGrace}
                    stripe={stripe}
                />
            );
        }

        // Default loading state while initializing
        return <UpdateCardLoadingState title={loadingTitle} />;
    };

    return (
        <section className="staticfield">
            <header>
                <div className="staticfield-label">Credit card</div>
                <ModalWrapper
                    openModalTrigger={openModalTrigger}
                    onCloseModal={() => {
                        handleCloseModal({ reload: false });
                    }}
                    title="Update credit card"
                    isLoading={modalLoadingState}
                    setModalIsOpen={setModalIsOpen}
                    modalIsOpen={modalIsOpen}
                >
                    {renderModalContent()}
                </ModalWrapper>
            </header>

            <CardFields user={user} />
        </section>
    );
}

UpdateCardModal.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.string,
        cardType: PropTypes.string,
        cardExpiry: PropTypes.string,
        cardLast4: PropTypes.string,
    }).isRequired,
    customerId: PropTypes.string,
    isGrace: PropTypes.bool,
    handleCloseModal: PropTypes.func.isRequired,
    handleNestedLoadingState: PropTypes.func.isRequired,
    setModalIsOpen: PropTypes.func.isRequired,
    modalIsOpen: PropTypes.bool.isRequired,
};
